/**
 * SarahHistory — 萨拉对话历史（IndexedDB 持久化）
 *
 * 数据结构：
 * - conversations: { id, title, created_at, updated_at }
 * - messages: { conversation_id, role, text, time, cost_coins, tokens }
 *
 * 策略：
 * - 每个用户独立库（key = 'sarah_history_user_{id}'）
 * - 限制每个会话最多 100 条消息
 * - 限制总共最多 50 个会话（超出按 updated_at 删旧）
 */

const DB_PREFIX = 'sarah_history_user_';

function dbName(userId) {
  return DB_PREFIX + userId;
}

function openDb(userId) {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName(userId), 1);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains('conversations')) {
        const cs = db.createObjectStore('conversations', { keyPath: 'id' });
        cs.createIndex('updated_at', 'updated_at');
      }
      if (!db.objectStoreNames.contains('messages')) {
        const ms = db.createObjectStore('messages', { keyPath: 'id', autoIncrement: true });
        ms.createIndex('conversation_id', 'conversation_id');
      }
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = (e) => reject(e.target.error);
  });
}

export async function saveMessage(userId, conversationId, message) {
  if (!conversationId) return;
  const db = await openDb(userId);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(['conversations', 'messages'], 'readwrite');
    const cs = tx.objectStore('conversations');

    // upsert conversation
    const getReq = cs.get(conversationId);
    getReq.onsuccess = () => {
      const conv = getReq.result || {
        id: conversationId,
        title: message.text?.slice(0, 30) || '新对话',
        created_at: message.time || new Date().toISOString(),
      };
      conv.updated_at = message.time || new Date().toISOString();
      // 用首条 user 消息做标题
      if (conv.title === '新对话' && message.role === 'user' && message.text) {
        conv.title = message.text.slice(0, 30);
      }
      cs.put(conv);
    };

    tx.objectStore('messages').add({
      conversation_id: conversationId,
      role: message.role,
      text: message.text,
      time: message.time || new Date().toISOString(),
      cost_coins: message.costCoins || 0,
      tokens: message.tokens || null,
    });

    tx.oncomplete = () => {
      db.close();
      // 异步清理
      cleanupOldConversations(userId, 50).catch(() => {});
      cleanupOldMessages(userId, conversationId, 100).catch(() => {});
      resolve();
    };
    tx.onerror = (e) => {
      db.close();
      reject(e.target.error);
    };
  });
}

export async function loadHistory(userId, conversationId) {
  if (!conversationId) return [];
  const db = await openDb(userId);
  return new Promise((resolve, reject) => {
    const tx = db.transaction('messages', 'readonly');
    const idx = tx.objectStore('messages').index('conversation_id');
    const req = idx.getAll(conversationId);
    req.onsuccess = () => {
      db.close();
      const msgs = (req.result || []).sort((a, b) => new Date(a.time) - new Date(b.time));
      resolve(msgs);
    };
    req.onerror = (e) => {
      db.close();
      reject(e.target.error);
    };
  });
}

export async function listConversations(userId) {
  const db = await openDb(userId);
  return new Promise((resolve, reject) => {
    const tx = db.transaction('conversations', 'readonly');
    const req = tx.objectStore('conversations').getAll();
    req.onsuccess = () => {
      db.close();
      const list = (req.result || []).sort((a, b) =>
        new Date(b.updated_at) - new Date(a.updated_at)
      );
      resolve(list);
    };
    req.onerror = (e) => {
      db.close();
      reject(e.target.error);
    };
  });
}

export async function deleteConversation(userId, conversationId) {
  const db = await openDb(userId);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(['conversations', 'messages'], 'readwrite');
    tx.objectStore('conversations').delete(conversationId);
    const msgIdx = tx.objectStore('messages').index('conversation_id');
    const cursorReq = msgIdx.openCursor(conversationId);
    cursorReq.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = (e) => { db.close(); reject(e.target.error); };
  });
}

async function cleanupOldConversations(userId, maxKeep) {
  const list = await listConversations(userId);
  if (list.length <= maxKeep) return;
  const toDelete = list.slice(maxKeep);
  for (const conv of toDelete) {
    await deleteConversation(userId, conv.id);
  }
}

async function cleanupOldMessages(userId, conversationId, maxKeep) {
  const msgs = await loadHistory(userId, conversationId);
  if (msgs.length <= maxKeep) return;
  const toDelete = msgs.slice(0, msgs.length - maxKeep);
  const db = await openDb(userId);
  const tx = db.transaction('messages', 'readwrite');
  for (const m of toDelete) {
    tx.objectStore('messages').delete(m.id);
  }
  await new Promise((resolve) => {
    tx.oncomplete = resolve;
  });
  db.close();
}